import _ from 'lodash'

/**
 * DepsTracker is an upgraded map to track dependencies.
 * Similar to a regular map, for each key(node) it store a set of keys(nodes) that depend on it.
 * However, there's also a reverse map - that stores for each node what nodes it depends on.
 * The second map(reveresed) exists only in order to delete dependencies for a node when needed.
 */
export class DepsTracker {
	/**
	 * Nodes dependencies map (direct-edges) is used to trigger re-execution of nodes when their dependencies change
	 */
	#depsMap: { [node: string]: Set<string> }
	/**
	 * Reverse dependencies map is used to find in O(1) all the nodes on which a node depends on.
	 * This is used to clean the depsMaps map
	 */
	#reverseDepsMap: { [node: string]: Set<string> }

	constructor() {
		this.#depsMap = {}
		this.#reverseDepsMap = {}
	}

	add(sourceNode: string, targetNode: string) {
		// Update #depsMap
		if (!(sourceNode in this.#depsMap)) {
			this.#depsMap[sourceNode] = new Set()
		}
		this.#depsMap[sourceNode].add(targetNode)
		// Update #reverseDepsMap
		if (!(targetNode in this.#reverseDepsMap)) {
			this.#reverseDepsMap[targetNode] = new Set()
		}
		this.#reverseDepsMap[targetNode].add(sourceNode)
	}

	/** Returns the nodes depending on a node. */
	getDependantNodes(sourceNode: string) {
		return this.#depsMap[sourceNode]
	}

	/** Deletes all edges pointing the provided node */
	deleteNodeDeps(targetNode: string) {
		const sourceNodes = this.#reverseDepsMap[targetNode]
		sourceNodes.forEach((sourceNode) => {
			this.#depsMap[sourceNode].delete(targetNode)
		})
		this.#reverseDepsMap[targetNode].clear()
	}

	/** Creates a new object with all the tracking edges and returns it. */
	getDependencies() {
		return _.mapValues(this.#depsMap, (s) => [...s])
	}
}
